import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@mui/material";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import GenericCrudTableModal from "@/components/tables/genericCrudTableModal";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import GenericNotification from "@/components/notification/genericNotification";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import {
  YOUTUBE_URL_REGEX,
  getYouTubeWatchUrl,
} from "@/helper/youTubePlayerUtils";
import {
  buildGlobalSettingRows,
  type GlobalSettingKey,
  type GlobalSettingRow,
} from "@/components/globalSettings/globalSettingDefinitions";
import { useGlobalSettings } from "@/components/globalSettings/useGlobalSettings";

const buildYouTubeUrlFormColumn = (
  key: GlobalSettingKey,
  t: (translationKey: string) => string,
): CrudColumnDef => ({
  id: key,
  accessorKey: key,
  header: t("youtube_video_url"),
  enableForCreate: true,
  rule: YOUTUBE_URL_REGEX,
  errorMessageValidation: t("youtube_invalid_url_error"),
  inputMode: "url",
});

export default function GlobalSettingsTable() {
  const { t } = useTranslation();
  usePageHeading(t("global_settings"));
  const { handleError, errorModal } = useErrorModal();
  const { settings, saveSetting } = useGlobalSettings(handleError);
  const [editedSetting, setEditedSetting] = useState<GlobalSettingRow | null>(
    null,
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState<
    string | null
  >(null);

  const rows = useMemo(
    () => (settings ? buildGlobalSettingRows(settings) : []),
    [settings],
  );

  const columns = useMemo<CrudColumnDef[]>(
    () => [
      {
        id: "portal",
        header: t("portal"),
        accessorFn: (row: GlobalSettingRow) => t(row.portalLabelKey),
      },
      {
        id: "setting",
        header: t("setting"),
        accessorFn: (row: GlobalSettingRow) => t(row.settingLabelKey),
      },
      {
        id: "value",
        header: t("value"),
        accessorKey: "value",
        Cell: ({ row }: { row: { original: GlobalSettingRow } }) => {
          const url = row.original.value;
          if (!url) {
            return null;
          }
          const watchUrl = getYouTubeWatchUrl(url);
          if (!watchUrl) {
            return url;
          }
          return (
            <Link href={watchUrl} target="_blank" rel="noopener noreferrer">
              {url}
            </Link>
          );
        },
      },
    ],
    [t],
  );

  const editedSettingKey = editedSetting?.key;
  const modalColumns = useMemo(
    () =>
      editedSettingKey
        ? [buildYouTubeUrlFormColumn(editedSettingKey, t)]
        : [],
    [editedSettingKey, t],
  );
  const modalPresetValues = useMemo(
    () => (editedSetting ? { [editedSetting.key]: editedSetting.value } : {}),
    [editedSetting],
  );

  const openEditModal = (row: { original: GlobalSettingRow }) => {
    setEditedSetting({ ...row.original }); 
    setIsEditModalOpen(true);
  };

  const submitSetting = async (
    values: Record<string, string | null | undefined>,
  ) => {
    if (!editedSetting) {
      return;
    }
    try {
      await saveSetting(editedSetting.key, values[editedSetting.key] ?? null);
      setIsEditModalOpen(false);
      setNotificationMessage(t("save_successful"));
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <>
      <GenericCrudTable
        entity="globalSetting"
        columns={columns}
        customTableData={rows}
        customEditHandler={openEditModal}
        disableAddItem
        hideDeleteButtonForRows
        disableGlobalFilter
        allowColumnFiltering={false}
        enablePagination={false}
      />
      <GenericCrudTableModal
        open={isEditModalOpen}
        modalMode="edit"
        columns={modalColumns}
        presetValues={modalPresetValues}
        title={
          editedSetting
            ? `${t(editedSetting.settingLabelKey)} – ${t(
                editedSetting.portalLabelKey,
              )}`
            : undefined
        }
        submitButtonText={t("save")}
        onSubmit={submitSetting}
        onClose={() => setIsEditModalOpen(false)}
        handleError={handleError}
      />
      <GenericNotification
        message={notificationMessage}
        variant="success"
        handleCloseSnackbar={() => setNotificationMessage(null)}
      />
      {errorModal}
    </>
  );
}
